import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  onSnapshot, 
  query, 
  orderBy, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  Timestamp, 
  where,
  getDocs,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { 
  CustomerCareTask, 
  CustomerCareReport, 
  Client, 
  UserRole,
  Settings,
  Invoice,
  Signatory,
  Employee,
  ServiceSchedule
} from '../types';
import { ServiceScheduleModal } from '../components/ServiceScheduleModal';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';
import { 
  TechServiceReportsSection,
  CustomerCareTechnicalReport
} from '../components/TechServiceReportsSection';
import { USER_ROLES } from '../constants';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Printer, 
  Mail, 
  FileText, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  ChevronDown,
  Calendar,
  Building2,
  Briefcase,
  History,
  FileSpreadsheet,
  Send,
  Loader2,
  PlusCircle,
  ClipboardList,
  MessageSquare,
  Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { format, startOfToday, endOfToday, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears, isWithinInterval } from 'date-fns';

const STATUS_OPTIONS = [
  "Assigned to Technician", 
  "Progress", 
  "Completed", 
  "Pending", 
  "Waiting Customer Approval", 
  "Proforma Sent", 
  "Approval Released", 
  "Service Memo Signed", 
  "Waiting Service Memo", 
  "Work Pending Due to Internal Procurement Proceedure"
];

const CustomerCareDashboard: React.FC = () => {
  const { profile, isAdmin, isManager, isCustomerCare } = useAuth();
  const [tasks, setTasks] = useState<CustomerCareTask[]>([]);
  const [reports, setReports] = useState<CustomerCareReport[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [currentReport, setCurrentReport] = useState<CustomerCareReport | null>(null);
  const [editingTask, setEditingTask] = useState<CustomerCareTask | null>(null);
  const [viewingTask, setViewingTask] = useState<CustomerCareTask | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<CustomerCareReport | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<CustomerCareTask | null>(null);
  const [techReportToDelete, setTechReportToDelete] = useState<CustomerCareTechnicalReport | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'report' | 'task' | 'tech_report' | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  
  // Technical & Service Reports States
  const [techServiceReports, setTechServiceReports] = useState<CustomerCareTechnicalReport[]>([]);
  const [showTechServiceReports, setShowTechServiceReports] = useState(false);
  const [isTechReportModalOpen, setIsTechReportModalOpen] = useState(false);
  const [isServiceReportModalOpen, setIsServiceReportModalOpen] = useState(false);
  const [editingTechReport, setEditingTechReport] = useState<CustomerCareTechnicalReport | null>(null);
  const [viewingTechReport, setViewingTechReport] = useState<CustomerCareTechnicalReport | null>(null);
  const [proformas, setProformas] = useState<Invoice[]>([]);
  const [signatories, setSignatories] = useState<Signatory[]>([]);

  // Service Schedules States
  const [serviceSchedules, setServiceSchedules] = useState<ServiceSchedule[]>([]);
  const [showServiceSchedules, setShowServiceSchedules] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleViewerOpen, setIsScheduleViewerOpen] = useState(false);
  const [selectedViewerClientId, setSelectedViewerClientId] = useState<string>('');
  const [editingSchedule, setEditingSchedule] = useState<ServiceSchedule | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  
  // Visibility States
  const [showTasks, setShowTasks] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [newReportTitle, setNewReportTitle] = useState('');
  const [isNamingModalOpen, setIsNamingModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    clientId: '',
    clientName: '',
    branch: '',
    userDepartment: '',
    task: '',
    status: '',
    comment: '',
    findings: '',
    recommendations: ''
  });

  const [customStatus, setCustomStatus] = useState('');
  const [allStatusOptions, setAllStatusOptions] = useState(STATUS_OPTIONS);

  // Filter State
  const [filters, setFilters] = useState({
    customer: '',
    startDate: '',
    endDate: '',
    taskType: '',
    preset: 'all'
  });

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!profile) return;

    const tasksUnsubscribe = onSnapshot(
      query(collection(db, 'customer_care_tasks'), orderBy('date', 'desc')),
      (snapshot) => {
        setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CustomerCareTask)));
        setLoading(false);
      },
      (error) => handleFirestoreError(error, OperationType.LIST, 'customer_care_tasks')
    );

    const reportsUnsubscribe = onSnapshot(
      query(collection(db, 'customer_care_reports'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CustomerCareReport)));
      },
      (error) => handleFirestoreError(error, OperationType.LIST, 'customer_care_reports')
    );

    const clientsUnsubscribe = onSnapshot(
      collection(db, 'clients'),
      (snapshot) => {
        setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
      }
    );

    const settingsUnsubscribe = onSnapshot(
      query(collection(db, 'settings'), limit(1)),
      (snapshot) => {
        if (!snapshot.empty) {
          setSettings({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Settings);
        }
      }
    );

    const techReportsUnsubscribe = onSnapshot(
      query(collection(db, 'customer_care_technical_reports'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setTechServiceReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CustomerCareTechnicalReport)));
      },
      (error) => handleFirestoreError(error, OperationType.LIST, 'customer_care_technical_reports')
    );

    const proformasUnsubscribe = onSnapshot(
      query(collection(db, 'invoices'), where('type', '==', 'proforma')),
      (snapshot) => {
        setProformas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice)));
      },
      (error) => {
        console.error("Failed to load proforma invoices", error);
      }
    );

    const signatoriesUnsubscribe = onSnapshot(
      collection(db, 'signatories'),
      (snapshot) => {
        setSignatories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Signatory)));
      },
      (error) => {
        console.error("Failed to load signatories", error);
      }
    );

    const serviceSchedulesUnsubscribe = onSnapshot(
      query(collection(db, 'service_schedules'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setServiceSchedules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ServiceSchedule)));
      },
      (error) => console.error("Failed to load service schedules", error)
    );

    const employeesUnsubscribe = onSnapshot(
      collection(db, 'employees'),
      (snapshot) => {
        setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Employee)));
      },
      (error) => console.error("Failed to load employees", error)
    );

    return () => {
      tasksUnsubscribe();
      reportsUnsubscribe();
      clientsUnsubscribe();
      settingsUnsubscribe();
      techReportsUnsubscribe();
      proformasUnsubscribe();
      signatoriesUnsubscribe();
      serviceSchedulesUnsubscribe();
      employeesUnsubscribe();
    };
  }, [profile]);

  // Update status options based on unique statuses in tasks
  useEffect(() => {
    const uniqueStatuses = Array.from(new Set([...STATUS_OPTIONS, ...tasks.map(t => t.status)]));
    setAllStatusOptions(uniqueStatuses);
  }, [tasks]);

  const generateServiceSchedulePDF = (schedule: ServiceSchedule) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 15;

    // Company Letterhead Header
    if (settings?.letterheadUrl) {
      try {
        const imgProps = doc.getImageProperties(settings.letterheadUrl);
        const imgWidth = pageWidth - 30;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        doc.addImage(settings.letterheadUrl, 'PNG', 15, 8, imgWidth, imgHeight);
        currentY = 8 + imgHeight + 8;
      } catch (e) {
        console.error("Error drawing letterhead image", e);
        doc.setFillColor(30, 58, 138);
        doc.rect(15, 10, pageWidth - 30, 16, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(settings?.companyName || 'BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 20, { align: 'center' });
        currentY = 34;
      }
    } else {
      doc.setFillColor(30, 58, 138);
      doc.rect(15, 10, pageWidth - 30, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(settings?.companyName || 'BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 20, { align: 'center' });
      currentY = 34;
    }

    // Client Name & Schedule Title
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`CLIENT NAME: ${(schedule.clientName || '').toUpperCase()}`, 15, currentY);

    currentY += 6;
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(schedule.quarterTitle.toUpperCase(), 15, currentY);

    if (schedule.scheduleDate) {
      currentY += 5;
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(`DATE: ${schedule.scheduleDate.toUpperCase()}`, 15, currentY);
    }

    currentY += 7;

    // Table
    const tableRows = (schedule.items || []).map((item, index) => [
      item.sn || (index + 1),
      item.branchName || '',
      item.noOfAcs || 0,
      item.noOfDays || 1,
      item.startingDate || '',
      item.assignedTechnicianNames || '',
      item.status || 'PENDING',
      item.reportReceived || 'NO',
      item.smemoReceived || 'NO'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [[
        'S/N',
        'BRANCHES',
        'NUMBER OF ACs',
        'NUMBER OF DAYS',
        'STARTING DATE',
        'ASSIGNED TECHNICIAN',
        'STATUS',
        'REPORT RECEIVED',
        'S/MEMO RECEIVED'
      ]],
      body: tableRows,
      theme: 'grid',
      margin: { left: 15, right: 15 },
      headStyles: {
        fillColor: [30, 58, 138],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: [30, 41, 59],
        valign: 'middle',
        cellPadding: 2
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 55 },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'center', cellWidth: 22 },
        4: { halign: 'center', cellWidth: 28 },
        5: { cellWidth: 55 },
        6: { halign: 'center', cellWidth: 25 },
        7: { halign: 'center', cellWidth: 24 },
        8: { halign: 'center', cellWidth: 24 }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 6) {
          const val = String(data.cell.raw).toUpperCase();
          if (val === 'COMPLETED') {
            data.cell.styles.textColor = [16, 185, 129];
            data.cell.styles.fontStyle = 'bold';
          } else if (val === 'IN PROGRESS') {
            data.cell.styles.textColor = [217, 119, 6];
            data.cell.styles.fontStyle = 'bold';
          } else if (val === 'PENDING') {
            data.cell.styles.textColor = [225, 29, 72];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 12;

    if (finalY + 25 < pageHeight) {
      doc.setFontSize(8.5);
      doc.setTextColor(71, 85, 105);
      doc.setFont('helvetica', 'bold');
      
      doc.text('Prepared By:', 15, finalY);
      doc.text('Approved By:', pageWidth - 80, finalY);
      
      doc.setFont('helvetica', 'normal');
      doc.text('Customer Care / Maintenance Dept.', 15, finalY + 4);
      doc.text('Technical Operations Manager', pageWidth - 80, finalY + 4);

      doc.setDrawColor(203, 213, 225);
      doc.line(15, finalY + 14, 75, finalY + 14);
      doc.line(pageWidth - 80, finalY + 14, pageWidth - 20, finalY + 14);

      doc.setFontSize(7.5);
      doc.text('Signature & Date', 15, finalY + 18);
      doc.text('Signature & Date', pageWidth - 80, finalY + 18);
    }

    doc.save(`Service_Schedule_${(schedule.clientName || 'Client').replace(/\s+/g, '_')}.pdf`);
  };

  const handleSaveServiceSchedule = async (scheduleData: Omit<ServiceSchedule, 'id' | 'createdAt'>) => {
    if (editingSchedule?.id) {
      await updateDoc(doc(db, 'service_schedules', editingSchedule.id), {
        ...scheduleData,
        updatedAt: serverTimestamp()
      });
      toast.success('Service Schedule updated successfully!');
    } else {
      await addDoc(collection(db, 'service_schedules'), {
        ...scheduleData,
        createdAt: serverTimestamp()
      });
      toast.success('Service Schedule generated and saved!');
    }
    setEditingSchedule(null);
  };

  const handleDeleteServiceSchedule = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this Service Schedule?')) {
      try {
        await deleteDoc(doc(db, 'service_schedules', id));
        toast.success('Service Schedule deleted');
      } catch (err) {
        toast.error('Failed to delete Service Schedule');
      }
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const taskDate = task.date.toDate();
      const matchesSearch = 
        task.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.task.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.status.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCustomer = !filters.customer || task.clientId === filters.customer;
      const matchesTaskType = !filters.taskType || task.task.toLowerCase().includes(filters.taskType.toLowerCase());

      const now = new Date();
      let matchesPreset = true;
      if (filters.preset === 'today') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfToday(), end: endOfToday() });
      } else if (filters.preset === 'this_week') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfWeek(now), end: endOfWeek(now) });
      } else if (filters.preset === 'last_week') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfWeek(subWeeks(now, 1)), end: endOfWeek(subWeeks(now, 1)) });
      } else if (filters.preset === 'this_month') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfMonth(now), end: endOfMonth(now) });
      } else if (filters.preset === 'last_month') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) });
      } else if (filters.preset === 'this_year') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfYear(now), end: endOfYear(now) });
      } else if (filters.preset === 'last_year') {
        matchesPreset = isWithinInterval(taskDate, { start: startOfYear(subYears(now, 1)), end: endOfYear(subYears(now, 1)) });
      }

      let matchesDateRange = true;
      if (filters.startDate && filters.endDate) {
        matchesDateRange = isWithinInterval(taskDate, { 
          start: new Date(filters.startDate), 
          end: new Date(filters.endDate + 'T23:59:59') 
        });
      }

      return matchesSearch && matchesCustomer && matchesTaskType && matchesPreset && matchesDateRange;
    });
  }, [tasks, searchTerm, filters]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || isSubmitting) return;

    const taskStatus = formData.status === 'other' ? customStatus : formData.status;
    if (!taskStatus) {
      toast.error('Please select or enter a status');
      return;
    }

    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const taskDate = new Date(formData.date);
      if (isNaN(taskDate.getTime())) {
        toast.error('Invalid date format');
        setIsSubmitting(false);
        return;
      }

      let nextSn = tasks.length + 1;
      if (!editingTask && tasks.length > 0) {
        const sns = tasks.map(t => t.sn || 0).filter(sn => typeof sn === 'number');
        const maxSn = sns.length > 0 ? Math.max(...sns) : 0;
        nextSn = maxSn + 1;
      }

      const taskData = {
        sn: editingTask ? editingTask.sn : nextSn,
        date: Timestamp.fromDate(taskDate),
        clientId: formData.clientId,
        clientName: formData.clientName,
        branch: formData.branch || '',
        userDepartment: formData.userDepartment || '',
        task: formData.task,
        status: taskStatus,
        comment: formData.comment || '',
        findings: formData.findings || '',
        recommendations: formData.recommendations || '',
        updatedAt: Timestamp.now(),
        createdBy: profile.uid,
        creatorName: profile.displayName || profile.email || 'Customer Care'
      };

      if (editingTask) {
        await updateDoc(doc(db, 'customer_care_tasks', editingTask.id), {
          ...taskData,
          createdAt: editingTask.createdAt
        });
        toast.success('Task updated successfully');
      } else {
        await addDoc(collection(db, 'customer_care_tasks'), {
          ...taskData,
          createdAt: Timestamp.now()
        });
        toast.success('Task recorded successfully');
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      console.error("DEBUG - Customer Care Write Error:", error);
      const errorMessage = error.message || 'Unknown database error';
      toast.error(`Database Error: ${errorMessage}`);
      try {
        handleFirestoreError(error, OperationType.WRITE, 'customer_care_tasks');
      } catch (err) {
        // Re-thrown error handled by infrastructure
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      clientId: '',
      clientName: '',
      branch: '',
      userDepartment: '',
      task: '',
      status: '',
      comment: '',
      findings: '',
      recommendations: ''
    });
    setCustomStatus('');
    setEditingTask(null);
  };

  const handleEdit = (task: CustomerCareTask) => {
    setEditingTask(task);
    setFormData({
      date: format(task.date.toDate(), 'yyyy-MM-dd'),
      clientId: task.clientId,
      clientName: task.clientName,
      branch: task.branch,
      userDepartment: task.userDepartment,
      task: task.task,
      status: STATUS_OPTIONS.includes(task.status) ? task.status : 'other',
      comment: task.comment,
      findings: task.findings || '',
      recommendations: task.recommendations || ''
    });
    if (!STATUS_OPTIONS.includes(task.status)) {
      setCustomStatus(task.status);
    }
    setIsModalOpen(true);
  };

  const handleViewTask = (task: CustomerCareTask) => {
    setViewingTask(task);
    setIsInfoModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (!isAdmin && !isManager) {
      toast.error('You do not have permission to delete tasks');
      return;
    }
    const task = tasks.find(t => t.id === id);
    if (task) {
      setTaskToDelete(task);
      setDeleteType('task');
      setIsDeleteModalOpen(true);
    }
  };

  const handleGenerateReport = () => {
    const tasksToReport = selectedTaskIds.length > 0 
      ? tasks.filter(t => selectedTaskIds.includes(t.id))
      : filteredTasks;

    if (tasksToReport.length === 0) {
      toast.error('No tasks selected to generate report');
      return;
    }

    setNewReportTitle(`Customer Care Report - ${format(new Date(), 'PPP')}`);
    setIsNamingModalOpen(true);
  };

  const finalizeReport = async () => {
    const tasksToReport = selectedTaskIds.length > 0 
      ? tasks.filter(t => selectedTaskIds.includes(t.id))
      : filteredTasks;

    try {
      setIsSubmitting(true);
      const reportData = {
        title: newReportTitle || `Customer Care Report - ${format(new Date(), 'PPP')}`,
        filterCriteria: filters,
        taskIds: tasksToReport.map(t => t.id),
        tasksSnapshot: tasksToReport,
        createdAt: Timestamp.now(),
        createdBy: profile?.uid || '',
        creatorName: profile?.displayName || profile?.email || ''
      };

      const docRef = await addDoc(collection(db, 'customer_care_reports'), reportData);
      const newReport = { id: docRef.id, ...reportData } as CustomerCareReport;
      setCurrentReport(newReport);
      setIsNamingModalOpen(false);
      setIsReportModalOpen(true);
      setSelectedTaskIds([]); // Clear selection after report generation
      setShowReports(true); // Show reports list when a new one is made
      toast.success('Report generated successfully');
    } catch (error) {
       handleFirestoreError(error, OperationType.CREATE, 'customer_care_reports');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedTaskIds.length === filteredTasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(filteredTasks.map(t => t.id));
    }
  };

  const exportTechServiceReportPDF = (report: CustomerCareTechnicalReport, isPrint = false) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    let currentY = 15;

    // Header with Letterhead or professional fallback
    if (settings?.letterheadUrl) {
      try {
        const imgProps = doc.getImageProperties(settings.letterheadUrl);
        const imgWidth = pageWidth - 40; // 297 - 40 = 257mm
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        doc.addImage(settings.letterheadUrl, 'PNG', 20, 10, imgWidth, imgHeight);
        currentY = 10 + imgHeight + 10;
      } catch (e) {
        console.error("Error drawing letterhead image", e);
        doc.setFillColor(30, 58, 138); // Navy
        doc.rect(20, 15, pageWidth - 40, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 25, { align: 'center' });
        currentY = 40;
      }
    } else {
      doc.setFillColor(30, 58, 138); // Navy
      doc.rect(20, 15, pageWidth - 40, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 25, { align: 'center' });
      currentY = 40;
    }

    // Client Name & Date
    doc.setTextColor(15, 23, 42); // slate-900
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`CLIENT: ${report.clientName.toUpperCase()}`, 20, currentY);
    
    const formattedDate = format(new Date(report.date), 'PPP').toUpperCase();
    doc.text(`DATE: ${formattedDate}`, pageWidth - 20, currentY, { align: 'right' });

    currentY += 10;

    // Subject
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`SUBJECT: ${report.subject.toUpperCase()}`, 20, currentY);
    
    // Underline subject
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.line(20, currentY + 1.5, pageWidth - 20, currentY + 1.5);

    currentY += 10;

    // Table mapping
    let columns: string[] = [];
    let tableRows: any[][] = [];

    if (report.type === 'technical') {
      columns = [
        'S/No', 
        'TYPE OF AIR-CONDITIONER', 
        'QTY', 
        'USER/DEPT', 
        'CHECKING RESULT', 
        'REMEDIAL RECOMMENDATIONS'
      ];
      tableRows = report.items.map((item, idx) => [
        item.sn || (idx + 1),
        item.typeOfAC,
        item.qty,
        item.userDept,
        item.checkingResult || '',
        item.remedialRecommendations
      ]);
    } else {
      columns = [
        'S/No', 
        'TYPE OF AIR-CONDITIONER', 
        'QTY', 
        'USER/DEPT', 
        'PERFORMANCE STATUS', 
        'REMEDIAL RECOMMENDATIONS'
      ];
      tableRows = report.items.map((item, idx) => [
        item.sn || (idx + 1),
        item.typeOfAC,
        item.qty,
        item.userDept,
        item.performanceStatus || '',
        item.remedialRecommendations
      ]);
    }

    // Render Table
    autoTable(doc, {
      startY: currentY,
      head: [columns],
      body: tableRows,
      theme: 'grid',
      margin: { left: 20, right: 20 },
      headStyles: { 
        fillColor: [30, 58, 138], 
        textColor: [255, 255, 255], 
        fontStyle: 'bold', 
        fontSize: 8.5,
        halign: 'center'
      },
      bodyStyles: { 
        fontSize: 8,
        textColor: [51, 65, 85],
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 60 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 40 },
        4: { cellWidth: 60 },
        5: { cellWidth: 67 }
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY;
    let nextY = finalY + 12;

    // Check height space for footer and signature
    if (nextY + 65 > pageHeight) {
      doc.addPage();
      nextY = 20;
    }

    // Post-table Text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);

    let footerParagraphs = [];
    if (report.type === 'technical') {
      footerParagraphs = [
        `We have attached our Proforma Invoice No. ${report.proformaNumber || '[Proforma Invoice No.]'} for the cost of repairing your split unit AC in the mentioned room for your perusal and approval.`,
        `Thanking you in advance for the opportunity of serving you, while anticipating the receipt of your L.P.O for the above works.`
      ];
    } else {
      footerParagraphs = [
        `Thanking you in advance for the opportunity of serving you, while anticipating the receipt of your L.P.O for the above works.`
      ];
    }

    footerParagraphs.forEach(para => {
      const splitText = doc.splitTextToSize(para, pageWidth - 40);
      doc.text(splitText, 20, nextY);
      nextY += (splitText.length * 4.5) + 3;
    });

    nextY += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Yours faithfully,', 20, nextY);
    nextY += 5;
    doc.text('BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', 20, nextY);
    nextY += 5;

    // Signature Area
    if (report.signatureUrl) {
      try {
        doc.addImage(report.signatureUrl, 'PNG', 20, nextY, 35, 12);
        nextY += 13;
      } catch (e) {
        console.error("Error drawing signature", e);
        nextY += 10;
      }
    } else {
      nextY += 12;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text(report.signatoryName.toUpperCase(), 20, nextY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(report.signatoryTitle, 20, nextY + 4);

    if (isPrint) {
      doc.autoPrint();
      window.open(doc.output('bloburl'), '_blank');
    } else {
      doc.save(`${report.reportNumber}_${report.clientName.replace(/\s+/g, '_')}.pdf`);
    }
  };

  const handleSaveTechReport = async (reportData: Partial<CustomerCareTechnicalReport>) => {
    if (!profile) return;
    const isEdit = !!reportData.id;
    try {
      toast.loading('Saving report...', { id: 'save-report' });
      const docData = {
        ...reportData,
        updatedAt: serverTimestamp(),
        createdBy: profile.uid,
        creatorName: profile.displayName || profile.email
      } as any;

      let fullReport: CustomerCareTechnicalReport;

      if (!isEdit) {
        docData.createdAt = serverTimestamp();
        const count = techServiceReports.filter(r => r.type === reportData.type).length + 1;
        const prefix = reportData.type === 'technical' ? 'TR' : 'SR';
        const year = new Date().getFullYear();
        docData.reportNumber = `${prefix}-${year}-${String(count).padStart(4, '0')}`;
        
        const docRef = await addDoc(collection(db, 'customer_care_technical_reports'), docData);
        toast.success('Report saved & generated successfully', { id: 'save-report' });
        
        fullReport = {
          id: docRef.id,
          ...docData,
          date: reportData.date || new Date().toISOString()
        } as CustomerCareTechnicalReport;
      } else {
        const docRef = doc(db, 'customer_care_technical_reports', reportData.id!);
        const originalId = reportData.id!;
        delete docData.id;
        await updateDoc(docRef, docData);
        toast.success('Report updated & generated successfully', { id: 'save-report' });
        
        fullReport = {
          id: originalId,
          ...docData,
          date: reportData.date || new Date().toISOString()
        } as CustomerCareTechnicalReport;
      }

      // Automatically generate the PDF report download!
      setTimeout(() => {
        exportTechServiceReportPDF(fullReport);
      }, 300);

    } catch (error) {
      console.error("Error saving report:", error);
      toast.error('Failed to save report', { id: 'save-report' });
      handleFirestoreError(error, isEdit ? OperationType.UPDATE : OperationType.CREATE, 'customer_care_technical_reports');
    }
  };

  const handleDeleteTechReport = async (id: string) => {
    const report = techServiceReports.find(r => r.id === id);
    if (report) {
      setTechReportToDelete(report);
      setDeleteType('tech_report');
      setIsDeleteModalOpen(true);
    }
  };

  const toggleSelectTask = (id: string) => {
    setSelectedTaskIds(prev => 
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
    );
  };

  const exportTechnicalPDF = (task: CustomerCareTask) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    let currentY = 10;

    // Header with Letterhead or professional fallback
    if (settings?.letterheadUrl) {
      const imgProps = doc.getImageProperties(settings.letterheadUrl);
      const imgWidth = pageWidth - 40;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
      doc.addImage(settings.letterheadUrl, 'PNG', 20, 10, imgWidth, imgHeight);
      currentY = 10 + imgHeight + 10;
    } else {
      doc.setFillColor(30, 58, 138); // Blue 900 fallback
      doc.rect(0, 0, pageWidth, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('TECHNICAL REPORT', pageWidth / 2, 25, { align: 'center' });
      currentY = 50;
    }

    doc.setTextColor(30, 58, 138);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TECHNICAL SERVICE REPORT', 20, currentY);
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Reference: SN-${task.sn}`, pageWidth - 20, currentY, { align: 'right' });
    doc.text(`Date: ${format(task.date.toDate(), 'PPP')}`, pageWidth - 20, currentY + 5, { align: 'right' });

    currentY += 15;
    doc.setTextColor(0, 0, 0);

    // Client Info Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('1. CLIENT INFORMATION', 20, currentY);
    doc.setDrawColor(200, 200, 200);
    doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Client Name: ${task.clientName}`, 25, currentY + 10);
    doc.text(`Branch: ${task.branch || 'Head Office'}`, 25, currentY + 17);
    doc.text(`Department: ${task.userDepartment || 'N/A'}`, 25, currentY + 24);

    currentY += 35;

    // Task Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('2. TASK / ISSUE REPORTED', 20, currentY);
    doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const splitTask = doc.splitTextToSize(task.task, pageWidth - 45);
    doc.text(splitTask, 25, currentY + 10);
    
    currentY += (splitTask.length * 5) + 15;

    // Status
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('3. WORK STATUS', 20, currentY);
    doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Current Status: ${task.status}`, 25, currentY + 10);
    
    currentY += 20;

    // Findings
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('4. FINDINGS', 20, currentY);
    doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const splitFindings = doc.splitTextToSize(task.findings || 'No specific findings recorded.', pageWidth - 45);
    doc.text(splitFindings, 25, currentY + 10);
    
    currentY += (splitFindings.length * 5) + 15;

    // Recommendations
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('5. RECOMMENDATIONS', 20, currentY);
    doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const splitRecs = doc.splitTextToSize(task.recommendations || 'No specific recommendations recorded.', pageWidth - 45);
    doc.text(splitRecs, 25, currentY + 10);
    
    currentY += (splitRecs.length * 5) + 20;

    // Comments
    if (task.comment) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('6. ADDITIONAL COMMENTS', 20, currentY);
      doc.line(20, currentY + 2, pageWidth - 20, currentY + 2);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const splitComment = doc.splitTextToSize(task.comment, pageWidth - 45);
      doc.text(splitComment, 25, currentY + 10);
      currentY += (splitComment.length * 5) + 20;
    }

    // Signatures
    const footerY = 260;
    doc.setDrawColor(30, 58, 138);
    doc.line(20, footerY - 5, pageWidth - 20, footerY - 5);
    
    doc.setFontSize(9);
    doc.text('REPORT PREPARED BY:', 20, footerY);
    doc.setFont('helvetica', 'bold');
    doc.text(task.creatorName.toUpperCase(), 20, footerY + 5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Role: Customer Care Executive`, 20, footerY + 10);
    doc.text(`Timestamp: ${format(task.createdAt.toDate(), 'PPP p')}`, 20, footerY + 15);

    doc.text('CLIENT ACKNOWLEDGMENT:', pageWidth / 2 + 10, footerY);
    doc.text('Authorized Signature: __________________________', pageWidth / 2 + 10, footerY + 10);
    doc.text('Date: _____________________________________', pageWidth / 2 + 10, footerY + 15);
    doc.text('Official Stamp & Designation', pageWidth / 2 + 10, footerY + 22);

    doc.save(`Technical_Report_SN_${task.sn}_${task.clientName.replace(/\s+/g, '_')}.pdf`);
  };

  const getPDFOutput = (report: { tasksSnapshot: CustomerCareTask[], createdAt: Timestamp, creatorName?: string }) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    let currentY = 15;

    // Header with Letterhead
    if (settings?.letterheadUrl) {
      try {
        const imgProps = doc.getImageProperties(settings.letterheadUrl);
        const imgWidth = 100; // Fixed width for report header
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        const imgX = (pageWidth - imgWidth) / 2;
        doc.addImage(settings.letterheadUrl, 'PNG', imgX, 10, imgWidth, imgHeight);
        currentY = Math.max(currentY, 10 + imgHeight + 5);
      } catch (e) {
        console.error("Failed to add letterhead image", e);
      }
    }

    doc.setTextColor(30, 58, 138);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Care Daily Tasks Report', pageWidth / 2, currentY, { align: 'center' });
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${format(report.createdAt.toDate(), 'PPP p')}`, pageWidth / 2, currentY + 7, { align: 'center' });
    
    if (report.creatorName) {
      doc.text(`Prepared by: ${report.creatorName}`, pageWidth / 2, currentY + 12, { align: 'center' });
    }

    const tableData = report.tasksSnapshot.map(task => [
      task.sn,
      format(task.date.toDate(), 'dd/MM/yyyy'),
      task.clientName,
      task.branch,
      task.userDepartment,
      task.task,
      task.status,
      task.comment
    ]);

    autoTable(doc, {
      startY: currentY + 20,
      head: [['S/N', 'Date', 'Client', 'Branch', 'Dept', 'Task', 'Status', 'Comment']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255] },
      styles: { fontSize: 8, overflow: 'linebreak' },
      columnStyles: {
        5: { cellWidth: 50 },
        6: { cellWidth: 40 },
        7: { cellWidth: 40 }
      }
    });

    return doc;
  };

  const exportPDF = (report: { tasksSnapshot: CustomerCareTask[], createdAt: Timestamp, creatorName?: string }) => {
    const doc = getPDFOutput(report);
    doc.save(`Customer_Care_Report_${format(report.createdAt.toDate(), 'yyyy-MM-dd')}.pdf`);
  };

  const handlePrint = (report: { tasksSnapshot: CustomerCareTask[], createdAt: Timestamp, creatorName?: string }) => {
    const doc = getPDFOutput(report);
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  const exportExcel = (report: CustomerCareReport | { tasksSnapshot: CustomerCareTask[], createdAt: Timestamp }) => {
    const sheetData = report.tasksSnapshot.map(task => ({
      'S/N': task.sn,
      'Date': format(task.date.toDate(), 'dd/MM/yyyy'),
      'Client Name': task.clientName,
      'Branch': task.branch,
      'Department': task.userDepartment,
      'Task': task.task,
      'Status': task.status,
      'Comment': task.comment
    }));

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, `Customer_Care_${report instanceof Object && 'title' in report ? 'Report' : 'Task'}_${format(report.createdAt.toDate(), 'yyyy-MM-dd')}.xlsx`);
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      toast.error('Please enter a recipient email');
      return;
    }
    if (!currentReport) return;

    try {
      toast.loading('Preparing report and sending email...');
      
      const doc = getPDFOutput(currentReport);
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailRecipient,
          subject: currentReport.title,
          body: `Dear recipient,\n\nPlease find the attached Customer Care Report.\n\nBest Regards,\n${profile?.displayName || 'Customer Care Team'}`,
          attachments: [
            {
              filename: `Customer_Care_Report_${format(currentReport.createdAt.toDate(), 'yyyy-MM-dd')}.pdf`,
              content: pdfBase64,
              encoding: 'base64'
            }
          ]
        })
      });

      if (response.ok) {
        toast.dismiss();
        toast.success('Email sent successfully');
        setIsEmailModalOpen(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send');
      }
    } catch (err: any) {
      toast.dismiss();
      toast.error(`Error sending email: ${err.message}`);
    }
  };

  const handleDeleteReport = (id: string) => {
    if (!isAdmin && !isManager) {
      toast.error('You do not have permission to delete reports');
      return;
    }
    const report = reports.find(r => r.id === id);
    if (report) {
      setReportToDelete(report);
      setDeleteType('report');
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      toast.loading('Deleting...', { id: 'delete-toast' });
      if (deleteType === 'task' && taskToDelete) {
        await deleteDoc(doc(db, 'customer_care_tasks', taskToDelete.id));
        toast.success('Task deleted successfully', { id: 'delete-toast' });
      } else if (deleteType === 'report' && reportToDelete) {
        await deleteDoc(doc(db, 'customer_care_reports', reportToDelete.id));
        toast.success('Report deleted successfully', { id: 'delete-toast' });
      } else if (deleteType === 'tech_report' && techReportToDelete) {
        await deleteDoc(doc(db, 'customer_care_technical_reports', techReportToDelete.id));
        toast.success('Technical report deleted successfully', { id: 'delete-toast' });
      }
      setIsDeleteModalOpen(false);
      setTaskToDelete(null);
      setReportToDelete(null);
      setTechReportToDelete(null);
      setDeleteType(null);
    } catch (error) {
       toast.error('Failed to delete', { id: 'delete-toast' });
       const path = deleteType === 'task' 
         ? 'customer_care_tasks' 
         : deleteType === 'report' 
           ? 'customer_care_reports' 
           : 'customer_care_technical_reports';
       handleFirestoreError(error, OperationType.DELETE, path);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Customer Care Dashboard</h1>
          <p className="text-slate-500 font-medium">Record daily tasks and generate customer relations reports.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <button 
            onClick={() => { setShowTasks(!showTasks); setShowReports(false); setShowTechServiceReports(false); setShowServiceSchedules(false); }}
            className={cn(
              "flex items-center gap-2 px-5 py-3 rounded-2xl font-bold transition-all active:scale-95 border-2",
              showTasks ? "bg-blue-50 border-blue-200 text-blue-900" : "bg-white border-slate-100 text-slate-500 hover:border-slate-200"
            )}
          >
            <ClipboardList className="w-5 h-5" />
            <span>{showTasks ? 'Hide Tasks' : 'View Tasks'}</span>
            <span className="ml-1 px-2 py-0.5 bg-blue-900 text-white rounded-lg text-[10px]">{tasks.length}</span>
          </button>
          <button 
            onClick={() => { setShowReports(!showReports); setShowTasks(false); setShowTechServiceReports(false); setShowServiceSchedules(false); }}
            className={cn(
              "flex items-center gap-2 px-5 py-3 rounded-2xl font-bold transition-all active:scale-95 border-2",
              showReports ? "bg-orange-50 border-orange-200 text-orange-900" : "bg-white border-slate-100 text-slate-500 hover:border-slate-200"
            )}
          >
            <History className="w-5 h-5" />
            <span>{showReports ? 'Hide Reports' : 'View Reports'}</span>
            <span className="ml-1 px-2 py-0.5 bg-orange-500 text-white rounded-lg text-[10px]">{reports.length}</span>
          </button>
          <button 
            onClick={() => { setShowTechServiceReports(!showTechServiceReports); setShowTasks(false); setShowReports(false); setShowServiceSchedules(false); }}
            className={cn(
              "flex items-center gap-2 px-5 py-3 rounded-2xl font-bold transition-all active:scale-95 border-2",
              showTechServiceReports ? "bg-teal-50 border-teal-200 text-teal-900" : "bg-white border-slate-100 text-slate-500 hover:border-slate-200"
            )}
          >
            <FileText className="w-5 h-5" />
            <span>Technical Reports</span>
            <span className="ml-1 px-2 py-0.5 bg-teal-600 text-white rounded-lg text-[10px]">{techServiceReports.length}</span>
          </button>
          <button 
            onClick={() => { setShowServiceSchedules(!showServiceSchedules); setShowTasks(false); setShowReports(false); setShowTechServiceReports(false); }}
            className={cn(
              "flex items-center gap-2 px-5 py-3 rounded-2xl font-bold transition-all active:scale-95 border-2",
              showServiceSchedules ? "bg-indigo-50 border-indigo-200 text-indigo-900" : "bg-white border-slate-100 text-slate-500 hover:border-slate-200"
            )}
          >
            <Calendar className="w-5 h-5 text-indigo-800" />
            <span>Service Schedules</span>
            <span className="ml-1 px-2 py-0.5 bg-indigo-800 text-white rounded-lg text-[10px]">{serviceSchedules.length}</span>
          </button>
          <button
            onClick={() => setIsScheduleViewerOpen(true)}
            className="flex items-center gap-2 px-5 py-3 bg-indigo-900 text-white rounded-2xl font-bold hover:bg-indigo-800 transition-all active:scale-95 shadow-md shadow-indigo-900/20 cursor-pointer"
          >
            <Eye className="w-5 h-5 text-indigo-200" />
            <span>View Service Schedule</span>
          </button>
          <button 
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 active:scale-95"
          >
            <Plus className="w-5 h-5" />
            <span>Record Task</span>
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      {!showTechServiceReports && !showServiceSchedules && (
        <div className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 space-y-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-blue-900" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">Filters & Search</span>
            </div>
            <label className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
              <input 
                type="checkbox"
                checked={selectedTaskIds.length === filteredTasks.length && filteredTasks.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-slate-300 text-blue-900 focus:ring-blue-900"
              />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Select All Filtered</span>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative group flex-1 min-w-[280px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-900 transition-colors" />
              <input 
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all placeholder:text-slate-400 text-sm font-medium"
              />
            </div>

            <select 
              value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-bold text-slate-700 text-sm min-w-[160px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <select 
              value={filters.preset}
              onChange={(e) => setFilters({ ...filters, preset: e.target.value })}
              className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-bold text-slate-700 text-sm min-w-[140px]"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
            </select>

            <div className="flex items-center gap-2 min-w-[320px]">
              <div className="relative flex-1">
                <input 
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all text-[11px] font-bold"
                />
              </div>
              <span className="text-slate-300 font-bold text-xs uppercase">to</span>
              <div className="relative flex-1">
                <input 
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all text-[11px] font-bold"
                />
              </div>
            </div>

            <button 
              onClick={() => setFilters({ customer: '', startDate: '', endDate: '', taskType: '', preset: 'all' })}
              className="px-6 py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all active:scale-95 whitespace-nowrap"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Batch Actions Bar */}
      <AnimatePresence>
        {selectedTaskIds.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-6 px-8 py-4 bg-blue-900 text-white rounded-full shadow-2xl shadow-blue-900/40 border border-white/10 backdrop-blur-md"
          >
            <div className="flex items-center gap-3 pr-6 border-r border-white/20">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-black">
                {selectedTaskIds.length}
              </div>
              <p className="text-sm font-bold">Tasks Selected</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleGenerateReport}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold transition-all active:scale-95"
              >
                <Download className="w-4 h-4" />
                Generate Selection Report
              </button>
              <button 
                onClick={() => setSelectedTaskIds([])}
                className="p-2 hover:bg-white/10 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showTechServiceReports && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Tasks List */}
        {showTasks && (
          <div className="xl:col-span-2 space-y-6 animate-in fade-in slide-in-from-left-4 duration-500">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-blue-900" />
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Daily Tasks</h2>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  onClick={handleGenerateReport}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-xs font-bold hover:bg-orange-600 transition-all"
                >
                  <Download className="w-4 h-4" />
                  Generate Report
                </button>
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{filteredTasks.length} total</span>
              </div>
            </div>
            <div className="space-y-4">
              {filteredTasks.map((task) => (
                <motion.div 
                  layout
                  key={task.id}
                  className={cn(
                    "group bg-white p-6 rounded-[32px] shadow-sm border transition-all hover:shadow-xl hover:shadow-blue-900/5",
                    selectedTaskIds.includes(task.id) ? "border-blue-400 ring-2 ring-blue-900/5 bg-blue-50/10" : "border-slate-100 hover:border-blue-200"
                  )}
                >
                  <div className="flex gap-4">
                    <div className="pt-1">
                      <input 
                        type="checkbox"
                        checked={selectedTaskIds.includes(task.id)}
                        onChange={() => toggleSelectTask(task.id)}
                        className="w-5 h-5 rounded-lg border-slate-300 text-blue-900 focus:ring-blue-900 transition-all cursor-pointer"
                      />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                          S/N: {task.sn}
                        </span>
                        <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                          <Calendar className="w-3 h-3" />
                          {format(task.date.toDate(), 'PPP')}
                        </span>
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                          {task.status}
                        </span>
                      </div>

                      <div>
                        <h3 className="text-lg font-black text-slate-900 group-hover:text-blue-900 transition-colors uppercase tracking-tight">
                          {task.clientName}
                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                            <Building2 className="w-3.5 h-3.5" />
                            {task.branch || 'Head Office'}
                          </p>
                          <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                            <Briefcase className="w-3.5 h-3.5" />
                            {task.userDepartment || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-2xl">
                        <p className="text-sm font-bold text-slate-800 leading-relaxed italic">"{task.task}"</p>
                      </div>

                      {task.comment && (
                        <div className="flex items-start gap-2">
                           <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5" />
                           <p className="text-sm text-slate-500 font-medium italic">{task.comment}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex md:flex-col justify-end gap-2 border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-6">
                      <button 
                        onClick={() => handleViewTask(task)}
                        className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all"
                        title="View Information"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleEdit(task)}
                        className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      {(isAdmin || isManager) && (
                        <button 
                          onClick={() => handleDelete(task.id)}
                          className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all md:opacity-0 md:group-hover:opacity-100"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}

              {filteredTasks.length === 0 && (
                <div className="bg-white p-12 rounded-[40px] text-center border-2 border-dashed border-slate-100">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ClipboardList className="w-10 h-10 text-slate-300" />
                  </div>
                  <p className="text-slate-400 font-bold">No tasks found matching your filters.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generated Reports History */}
        {showReports && (
          <div className={cn(
            "space-y-6 animate-in fade-in slide-in-from-right-4 duration-500",
            !showTasks ? "xl:col-span-3" : "xl:col-span-1"
          )}>
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-blue-900" />
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Reports Archive</h2>
            </div>

            <div className={cn(
              "grid gap-4 overflow-y-auto pr-2",
              !showTasks ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1",
              showTasks ? "max-h-[1000px]" : ""
            )}>
              {reports.map((report) => (
                <div 
                  key={report.id}
                  className="bg-white p-5 rounded-[28px] shadow-sm border border-slate-100 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="bg-orange-50 p-3 rounded-2xl">
                      <FileText className="w-6 h-6 text-orange-600" />
                    </div>
                    {(isAdmin || isManager) && (
                      <button 
                        onClick={() => handleDeleteReport(report.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-all md:opacity-0 md:group-hover:opacity-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <h3 className="font-black text-slate-900 leading-tight mb-1">{report.title}</h3>
                  <p className="text-xs text-slate-400 font-bold mb-4 uppercase tracking-widest flex items-center gap-1.5">
                    <PlusCircle className="w-3 h-3" />
                    {format(report.createdAt.toDate(), 'PP')} by {report.creatorName}
                  </p>

                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => { setCurrentReport(report); setIsReportModalOpen(true); }}
                      className="flex items-center justify-center gap-2 py-2 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-xl text-xs font-bold transition-all"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View Report
                    </button>
                    <button 
                      onClick={() => exportPDF(report)}
                      className="flex items-center justify-center gap-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-bold transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                      PDF
                    </button>
                    <button 
                      onClick={() => exportExcel(report)}
                      className="flex items-center justify-center gap-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-bold transition-all"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5" />
                      Excel
                    </button>
                    <button 
                      onClick={() => handlePrint(report)}
                      className="flex items-center justify-center gap-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-bold transition-all"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Print
                    </button>
                    <button 
                      onClick={() => { setCurrentReport(report); setIsEmailModalOpen(true); }}
                      className="flex items-center justify-center gap-2 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-xs font-bold transition-all"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Email
                    </button>
                  </div>
                </div>
              ))}

              {reports.length === 0 && (
                <p className="text-slate-400 text-center py-12 italic col-span-full">No reports generated yet.</p>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {showTechServiceReports && (
        <TechServiceReportsSection
          reports={techServiceReports}
          proformas={proformas}
          clients={clients}
          signatories={signatories}
          settings={settings}
          onSaveReport={handleSaveTechReport}
          onDeleteReport={handleDeleteTechReport}
          onExportPDF={exportTechServiceReportPDF}
        />
      )}

      {showServiceSchedules && (
        <div className="space-y-6 animate-in fade-in duration-500">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-indigo-900 to-blue-900 text-white p-6 rounded-[32px] shadow-lg">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-5 h-5 text-indigo-300" />
                <h2 className="text-xl font-black tracking-tight">Client Service Schedules</h2>
              </div>
              <p className="text-xs text-indigo-200 font-medium">Generate, print, and track quarterly AC maintenance service schedules in landscape with letterhead.</p>
            </div>
            <button
              onClick={() => { setEditingSchedule(null); setIsScheduleModalOpen(true); }}
              className="px-6 py-3 bg-white text-indigo-900 hover:bg-indigo-50 rounded-2xl font-bold transition-all shadow-md flex items-center gap-2 text-xs uppercase tracking-wider cursor-pointer"
            >
              <Plus className="w-4 h-4 text-indigo-900" />
              <span>Generate Service Schedule</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceSchedules.map(schedule => {
              const totalAcs = (schedule.items || []).reduce((acc, item) => acc + (item.noOfAcs || 0), 0);
              const branchCount = (schedule.items || []).length;
              const completedCount = (schedule.items || []).filter(i => i.status === 'COMPLETED').length;

              return (
                <div key={schedule.id} className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-wider">
                        {schedule.clientName || 'Client Schedule'}
                      </span>
                      <span className="text-[10px] text-slate-400 font-bold">
                        {schedule.scheduleDate || 'No Date'}
                      </span>
                    </div>

                    <h3 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug">
                      {schedule.quarterTitle}
                    </h3>

                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
                      <div className="bg-slate-50 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Branches</p>
                        <p className="text-xs font-black text-slate-800">{branchCount}</p>
                      </div>
                      <div className="bg-blue-50/60 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-blue-600 uppercase">Total ACs</p>
                        <p className="text-xs font-black text-blue-900">{totalAcs}</p>
                      </div>
                      <div className="bg-emerald-50/60 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-emerald-600 uppercase">Completed</p>
                        <p className="text-xs font-black text-emerald-900">{completedCount}/{branchCount}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        setSelectedViewerClientId(schedule.clientId || '');
                        setIsScheduleViewerOpen(true);
                      }}
                      className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 border border-indigo-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
                      title="View Service Schedule Details"
                    >
                      <Eye className="w-3.5 h-3.5 text-indigo-700" />
                      <span>View</span>
                    </button>
                    <button
                      onClick={() => generateServiceSchedulePDF(schedule)}
                      className="flex-1 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      <span>Print PDF</span>
                    </button>
                    <button
                      onClick={() => { setEditingSchedule(schedule); setIsScheduleModalOpen(true); }}
                      className="p-2 text-slate-600 hover:text-blue-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                      title="Edit Schedule"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {(isAdmin || isManager || isCustomerCare) && (
                      <button
                        onClick={() => handleDeleteServiceSchedule(schedule.id)}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all cursor-pointer"
                        title="Delete Schedule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {serviceSchedules.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white border-2 border-dashed border-slate-200 rounded-3xl space-y-3">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="text-base font-bold text-slate-700">No Service Schedules Generated Yet</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Click "Generate Service Schedule" above to create client quarterly service schedules with branch AC quantities and assigned technicians.
                </p>
                <button
                  onClick={() => { setEditingSchedule(null); setIsScheduleModalOpen(true); }}
                  className="px-5 py-2.5 bg-blue-900 text-white rounded-xl text-xs font-bold hover:bg-blue-800 transition-all inline-flex items-center gap-2 mt-2 cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Generate Schedule
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Schedule Modal */}
      <ServiceScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => { setIsScheduleModalOpen(false); setEditingSchedule(null); }}
        clients={clients}
        employees={employees}
        onSave={handleSaveServiceSchedule}
        editingSchedule={editingSchedule}
        settings={settings}
        onPrintPDF={generateServiceSchedulePDF}
      />

      {/* Service Schedule Viewer Modal */}
      <ServiceScheduleViewerModal
        isOpen={isScheduleViewerOpen}
        onClose={() => setIsScheduleViewerOpen(false)}
        defaultClientId={selectedViewerClientId}
      />

      {/* Report Naming Modal */}
      <AnimatePresence>
        {isNamingModalOpen && (
          <div className="fixed inset-0 z-[115] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsNamingModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl p-10"
            >
              <div className="mb-8">
                <h2 className="text-2xl font-black text-slate-900 tracking-tight text-center">Name Your Report</h2>
                <p className="text-slate-500 font-medium text-center">Give this report a descriptive name for easy tracking.</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Report Title</label>
                  <input 
                    type="text"
                    placeholder="e.g. Weekly Activity Report - May"
                    value={newReportTitle}
                    onChange={(e) => setNewReportTitle(e.target.value)}
                    autoFocus
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-bold"
                  />
                </div>

                <div className="flex gap-4">
                  <button 
                    onClick={() => setIsNamingModalOpen(false)}
                    disabled={isSubmitting}
                    className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-200 transition-all shadow-md active:scale-95 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={finalizeReport}
                    disabled={isSubmitting}
                    className="flex-2 py-4 bg-orange-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      'Confirm & Generate'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Record Task Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleCreateTask} className="flex flex-col h-full max-h-[90vh]">
                <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-blue-900 text-white">
                  <div>
                    <h2 className="text-2xl font-black tracking-tight">{editingTask ? 'Edit Task' : 'Record Daily Task'}</h2>
                    <p className="text-blue-200 text-sm font-medium tracking-tight">Customer Care Support Log</p>
                  </div>
                  <button type="button" onClick={() => setIsModalOpen(false)} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-8 overflow-y-auto space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">S/N</label>
                      <input 
                        type="text" 
                        value={editingTask ? editingTask.sn : tasks.length + 1}
                        disabled
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Date</label>
                      <input 
                        type="date" 
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Client Name</label>
                    <select 
                      value={formData.clientId}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setFormData({ ...formData, clientId: e.target.value, clientName: client?.name || '' });
                      }}
                      required
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                    >
                      <option value="">Select Client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Branch</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Kariakoo / Main"
                        value={formData.branch}
                        onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Department</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Sales / Support"
                        value={formData.userDepartment}
                        onChange={(e) => setFormData({ ...formData, userDepartment: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Task Description</label>
                    <textarea 
                      placeholder="What was the task performed?"
                      value={formData.task}
                      onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                      required
                      rows={3}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Task Status</label>
                    <div className="space-y-3">
                      <select 
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-bold"
                      >
                        <option value="">Select Status...</option>
                        {allStatusOptions.filter(o => STATUS_OPTIONS.includes(o)).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        <option value="other">+ Type custom status...</option>
                      </select>
                      
                      {formData.status === 'other' && (
                        <input 
                          type="text"
                          placeholder="Enter custom status..."
                          value={customStatus}
                          onChange={(e) => setCustomStatus(e.target.value)}
                          required
                          className="w-full px-5 py-4 bg-blue-50 border border-blue-100 rounded-2xl font-bold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Findings</label>
                    <textarea 
                      placeholder="What were your technical findings?"
                      value={formData.findings}
                      onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                      rows={3}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Recommendations</label>
                    <textarea 
                      placeholder="Provide technical recommendations..."
                      value={formData.recommendations}
                      onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                      rows={3}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Comments</label>
                    <input 
                      type="text" 
                      placeholder="Any additional remarks?"
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                    />
                  </div>
                </div>

                <div className="p-8 border-t border-slate-100 flex gap-4">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-3 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Recording...</span>
                      </>
                    ) : (
                      editingTask ? 'Update Task' : 'Record Task'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Report View Modal */}
      <AnimatePresence>
        {isReportModalOpen && currentReport && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReportModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-blue-900 text-white">
                <div>
                  <h2 className="text-2xl font-black tracking-tight">{currentReport.title}</h2>
                  <p className="text-blue-100 text-sm font-medium">Report generated on {format(currentReport.createdAt.toDate(), 'PPP p')}</p>
                </div>
                <button 
                  onClick={() => setIsReportModalOpen(false)}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 space-y-8">
                {/* Statistics Box */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Total Tasks</p>
                    <p className="text-3xl font-black text-slate-900">{currentReport.tasksSnapshot.length}</p>
                  </div>
                  <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Creator</p>
                    <p className="text-xl font-black text-slate-900 truncate">{currentReport.creatorName}</p>
                  </div>
                  <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Time Range</p>
                    <p className="text-sm font-black text-slate-900">
                      {currentReport.filterCriteria.startDate ? format(new Date(currentReport.filterCriteria.startDate), 'MMM d') : 'All'} - 
                      {currentReport.filterCriteria.endDate ? format(new Date(currentReport.filterCriteria.endDate), 'MMM d, yyyy') : 'Present'}
                    </p>
                  </div>
                </div>

                {/* Tasks List within Report */}
                <div className="space-y-4">
                  <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Included Tasks Summary
                  </h3>
                  <div className="border border-slate-100 rounded-[32px] overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-400">Date</th>
                          <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-400">Client</th>
                          <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-400">Task</th>
                          <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {currentReport.tasksSnapshot.map((task) => (
                          <tr key={task.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 text-xs font-bold text-slate-600">{format(task.date.toDate(), 'dd/MM/yyyy')}</td>
                            <td className="px-6 py-4 text-xs font-black text-slate-900 uppercase">{task.clientName}</td>
                            <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-[200px] truncate">{task.task}</td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[9px] font-black uppercase tracking-widest">
                                {task.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-4">
                <button 
                  onClick={() => exportPDF(currentReport)}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-4 bg-red-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 active:scale-95"
                >
                  <Download className="w-5 h-5" />
                  Download PDF
                </button>
                <button 
                  onClick={() => exportExcel(currentReport)}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Excel Sheet
                </button>
                <button 
                  onClick={() => { setIsEmailModalOpen(true); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 active:scale-95"
                >
                  <Mail className="w-5 h-5" />
                  Email Report
                </button>
                <button 
                  onClick={() => handlePrint(currentReport)}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-4 bg-slate-800 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-700 transition-all shadow-lg shadow-slate-800/20 active:scale-95"
                >
                  <Printer className="w-5 h-5" />
                  Print Report
                </button>
                <button 
                   onClick={() => setIsReportModalOpen(false)}
                  className="min-w-[100px] py-4 px-8 bg-white border border-slate-200 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Email Modal */}
      <AnimatePresence>
        {isEmailModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEmailModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl p-10"
            >
              <div className="mb-8">
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Send Report</h2>
                <p className="text-slate-500 font-medium">Enter the recipient's email address below.</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Recipient Email</label>
                  <input 
                    type="email"
                    placeholder="example@company.com"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all"
                  />
                </div>

                <button 
                  onClick={handleSendEmail}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 active:scale-95"
                >
                  <Send className="w-5 h-5" />
                  Send Report Now
                </button>

                <button 
                  onClick={() => setIsEmailModalOpen(false)}
                  className="w-full py-4 text-slate-400 font-bold hover:text-slate-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Task Info Modal */}
      <AnimatePresence>
        {isInfoModalOpen && viewingTask && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsInfoModalOpen(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-3xl bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-blue-900 text-white">
                <div>
                  <h2 className="text-2xl font-black tracking-tight uppercase">Task Details</h2>
                  <p className="text-blue-200 text-sm font-medium tracking-tight">Viewing S/N: {viewingTask.sn}</p>
                </div>
                <button onClick={() => setIsInfoModalOpen(false)} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8 overflow-y-auto max-h-[70vh] space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Record Date</label>
                      <p className="text-lg font-black text-slate-900 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-900" />
                        {format(viewingTask.date.toDate(), 'PPP')}
                      </p>
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Client Name</label>
                      <p className="text-xl font-black text-blue-900">{viewingTask.clientName}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Branch</label>
                        <p className="font-bold text-slate-700 flex items-center gap-2">
                           <Building2 className="w-4 h-4 text-slate-400" />
                           {viewingTask.branch || 'Head Office'}
                        </p>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Department</label>
                        <p className="font-bold text-slate-700 flex items-center gap-2">
                           <Briefcase className="w-4 h-4 text-slate-400" />
                           {viewingTask.userDepartment || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Current Status</label>
                      <span className="inline-flex px-4 py-2 bg-emerald-50 text-emerald-700 rounded-2xl text-xs font-black uppercase tracking-widest border border-emerald-100">
                        {viewingTask.status}
                      </span>
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Task Description</label>
                      <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                        <p className="text-slate-800 font-bold italic leading-relaxed">"{viewingTask.task}"</p>
                      </div>
                    </div>

                    {viewingTask.comment && (
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Comments</label>
                        <p className="text-slate-500 font-medium italic">{viewingTask.comment}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Findings & Recommendations in View */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-slate-100 pt-8">
                   <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block">Findings</label>
                      <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100 min-h-[100px]">
                        <p className="text-slate-700 font-medium leading-relaxed whitespace-pre-wrap">
                          {viewingTask.findings || 'No findings recorded.'}
                        </p>
                      </div>
                   </div>
                   <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block">Recommendations</label>
                      <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100 min-h-[100px]">
                        <p className="text-slate-700 font-medium leading-relaxed whitespace-pre-wrap">
                          {viewingTask.recommendations || 'No recommendations recorded.'}
                        </p>
                      </div>
                   </div>
                </div>

                <div className="pt-8 border-t border-slate-100 flex flex-wrap items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-300">
                  <div className="flex items-center gap-2">
                    <History className="w-3 h-3" />
                    Recorded by {viewingTask.creatorName} at {format(viewingTask.createdAt.toDate(), 'p PP')}
                  </div>
                </div>
              </div>

              <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-4">
                <button 
                  onClick={() => setIsInfoModalOpen(false)}
                  className="flex-1 min-w-[120px] py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Close
                </button>
                <button 
                  onClick={() => exportTechnicalPDF(viewingTask)}
                  className="flex-2 min-w-[180px] py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Technical Report (PDF)
                </button>
                <div className="flex w-full md:w-auto gap-4">
                  <button 
                    onClick={() => exportPDF({ tasksSnapshot: [viewingTask], createdAt: Timestamp.now(), creatorName: profile?.displayName || profile?.email || '' })}
                    className="flex-1 py-4 px-6 bg-red-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Daily PDF
                  </button>
                  <button 
                    onClick={() => exportExcel({ tasksSnapshot: [viewingTask], createdAt: Timestamp.now() })}
                    className="flex-1 py-4 px-6 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDeleteModalOpen(false)}
              className="absolute inset-0 bg-red-950/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl p-10 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-red-500" />
              
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
                  <Trash2 className="w-10 h-10 text-red-500" />
                </div>
                
                <div className="space-y-2">
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Confirm Delete</h2>
                  <p className="text-slate-500 font-medium leading-relaxed">
                    Are you sure you want to delete this {deleteType}? This action is permanent and cannot be undone.
                  </p>
                  {deleteType === 'task' && taskToDelete && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 italic text-sm text-slate-600">
                      "{taskToDelete.task}"
                    </div>
                  )}
                  {deleteType === 'report' && reportToDelete && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 font-bold text-sm text-slate-600">
                      {reportToDelete.title}
                    </div>
                  )}
                  {deleteType === 'tech_report' && techReportToDelete && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 font-bold text-sm text-slate-600">
                      {techReportToDelete.subject} ({techReportToDelete.reportNumber})
                    </div>
                  )}
                </div>

                <div className="flex w-full gap-4 pt-4">
                  <button 
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-200 transition-all active:scale-95 shadow-md"
                  >
                    No
                  </button>
                  <button 
                    onClick={confirmDelete}
                    className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-red-700 transition-all active:scale-95 shadow-xl shadow-red-600/20"
                  >
                    YES
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerCareDashboard;
